import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Add, Visibility } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { validationService } from '../../services/validationService';
import { ValidationStatus } from '../../types/validation.types';
import ValidationStatusBadge from '../../components/validation/ValidationStatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import TriggerValidationDialog from '../../components/validation/TriggerValidationDialog';

const ValidationHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(parseInt(searchParams.get('page') || '0'));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('size') || '25'));
  const [statusFilter, setStatusFilter] = useState<ValidationStatus | ''>((searchParams.get('status') as ValidationStatus) || '');
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['allValidations', page, rowsPerPage, statusFilter],
    queryFn: () => validationService.getAllValidations({
      page,
      size: rowsPerPage,
      status: statusFilter || undefined,
    }),
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setSearchParams({ page: newPage.toString(), size: rowsPerPage.toString() });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    setSearchParams({ page: '0', size: newSize.toString() });
  };

  const handleStatusFilterChange = (status: ValidationStatus | '') => {
    setStatusFilter(status);
    setPage(0);
    if (status) {
      setSearchParams({ page: '0', size: rowsPerPage.toString(), status });
    } else {
      setSearchParams({ page: '0', size: rowsPerPage.toString() });
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load validation history" />;
  if (!data) return <ErrorMessage message="No data available" />;

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Page SEO Validation
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Performs live technical compliance checks by crawling pages in real-time. Validates HTTP status, canonical URLs, meta tags, structured data, and 10+ other SEO rules.
        </Typography>
      </Box>

      <Box mb={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
        >
          New Validation
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => handleStatusFilterChange(e.target.value as ValidationStatus | '')}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="RUNNING">Running</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Page URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Failures</TableCell>
              <TableCell align="center">Warnings</TableCell>
              <TableCell align="center">Passed</TableCell>
              <TableCell>Run At</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.content.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                    No validation runs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.content.map((run) => (
                <TableRow key={run.id} hover>
                  <TableCell>#{run.id}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap>
                      {run.pageUrl}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ValidationStatusBadge status={run.status} />
                  </TableCell>
                  <TableCell align="center">
                    {run.failCount > 0 ? (
                      <Chip
                        label={run.failCount}
                        size="small"
                        color="error"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">0</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {run.warnCount > 0 ? (
                      <Chip
                        label={run.warnCount}
                        size="small"
                        color="warning"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">0</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {run.passCount > 0 ? (
                      <Chip
                        label={run.passCount}
                        size="small"
                        color="success"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">0</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(run.runAt).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {run.durationMs ? `${(run.durationMs / 1000).toFixed(2)}s` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/validation/runs/${run.id}`)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Trigger Validation Dialog */}
      <TriggerValidationDialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
      />
    </Box>
  );
};

export default ValidationHistoryPage;
